import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  Calendar,
  CreditCard,
  Info,
  Layers,
  PackageOpen,
  Percent,
  QrCode,
  Receipt,
  Send,
  ShoppingBasket,
  Smartphone,
  TrendingUp,
  UsersRound,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Spinner } from '@/components/ui/Spinner';
import { getAnalytics, type AnalyticsPayload, type AnalyticsPeriod } from '@/services/admin';
import { formatRub, paymentMethodLabels } from '@/utils/format';

const BRAND = '#4f62e0';
const PALETTE = ['#4f62e0', '#22c55e', '#f59e0b', '#ef4444', '#0ea5e9', '#a855f7', '#14b8a6', '#f97316'];
const SOURCE_COLORS: Record<string, string> = { CASH: '#4f62e0', APP: '#22c55e' };

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const useCustom = Boolean(from && to);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/analytics', useCustom ? `${from}..${to}` : period],
    queryFn: () =>
      getAnalytics(useCustom ? { from, to } : { period }),
  });

  const resetCustom = () => {
    setFrom('');
    setTo('');
  };

  if (isLoading) return <Spinner label="Собираем метрики" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Аналитика</h1>
          <p className="text-sm text-slate-500">
            {formatShortDate(data.range.from)} — {formatShortDate(data.range.to)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className={`flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar ${useCustom ? 'opacity-50' : ''}`}>
            {(['today', '7d', '30d', '90d'] as const).map((p) => (
              <button
                key={p}
                onClick={() => {
                  setPeriod(p);
                  resetCustom();
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap shrink-0 ${
                  !useCustom && period === p
                    ? 'bg-white shadow text-brand-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {p === 'today' ? 'Сегодня' : p === '7d' ? '7 дней' : p === '30d' ? '30 дней' : '90 дней'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
            <Calendar size={15} className="text-slate-400" />
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-sm outline-none bg-transparent"
              max={to || undefined}
            />
            <span className="text-slate-400 text-sm">—</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-sm outline-none bg-transparent"
              min={from || undefined}
            />
            {useCustom && (
              <button
                onClick={resetCustom}
                className="text-slate-400 hover:text-slate-700"
                title="Сбросить"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </header>

      <KpiGrid data={data} />

      <PaymentMethodsTiles methods={data.paymentMethods} />

      <ExpensesBlock data={data} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5 lg:col-span-2">
          <SectionHeader
            title="Оборот и заказы по дням"
            subtitle="Выручка и расходы (₽), количество заказов"
          />
          <div className="h-80">
            <ResponsiveContainer>
              <AreaChart
                data={data.daily.map((d) => ({
                  ...d,
                  revenue: d.revenue / 100,
                  expenses: (d.expenses ?? 0) / 100,
                }))}
              >
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}к`}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(v: number, name: string) =>
                    name === 'orders' ? `${v} шт` : `${v.toLocaleString('ru-RU')} ₽`
                  }
                  labelFormatter={(l) => shortDay(String(l))}
                />
                <Legend
                  formatter={(v) =>
                    v === 'revenue' ? 'Выручка' : v === 'expenses' ? 'Расходы' : 'Заказов'
                  }
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke={BRAND}
                  fill="url(#rev)"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="expenses"
                  stroke="#f43f5e"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="orders"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5">
          <SectionHeader title="Источники заказов" subtitle="Касса vs. приложение" />
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data.sources.map((s) => ({
                    name: s.source === 'CASH' ? 'Касса' : 'Приложение',
                    key: s.source,
                    value: s.revenue / 100,
                    count: s.count,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {data.sources.map((s, i) => (
                    <Cell key={i} fill={SOURCE_COLORS[s.source] ?? PALETTE[i]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number, _n, p) =>
                    [`${v.toLocaleString('ru-RU')} ₽ · ${p.payload.count} шт`, p.payload.name]
                  }
                />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
            {data.sources.map((s) => (
              <div key={s.source} className="rounded-lg bg-slate-50 p-3">
                <div className="text-xs text-slate-500">{s.source === 'CASH' ? 'Касса' : 'Приложение'}</div>
                <div className="font-semibold">{formatRub(s.revenue)}</div>
                <div className="text-xs text-slate-400">{s.count} заказов</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-5">
        <SectionHeader title="Средний чек по дням" subtitle="Динамика среднего чека (₽)" />
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={data.avgCheckDynamic.map((d) => ({ ...d, avgCheck: d.avgCheck / 100 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(1)}к`} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: number) => `${v.toLocaleString('ru-RU')} ₽`}
                labelFormatter={(l) => shortDay(String(l))}
              />
              <Line type="monotone" dataKey="avgCheck" stroke="#22c55e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-5">
        <SectionHeader title="Активность по часам" subtitle="Количество заказов" />
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={data.hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="hour" tickFormatter={(v) => `${v}:00`} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                formatter={(v: number, _n, p) =>
                  [`${v} заказов · ${formatRub(p.payload.revenue)}`, `${p.payload.hour}:00`]
                }
              />
              <Bar dataKey="count" fill={BRAND} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <SectionHeader title="Топ-10 блюд" subtitle="По количеству продаж" />
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart
                layout="vertical"
                data={data.topDishes.map((d) => ({
                  ...d,
                  total: d.total / 100,
                  profitRub: (d.profit ?? 0) / 100,
                  hasCost: (d.cost ?? 0) > 0,
                }))}
                margin={{ left: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number, _n, p) => [
                    `${v} шт · ${p.payload.total.toLocaleString('ru-RU')} ₽` +
                      (p.payload.hasCost
                        ? ` · прибыль ${p.payload.profitRub.toLocaleString('ru-RU')} ₽`
                        : ''),
                    p.payload.name,
                  ]}
                />
                <Bar dataKey="quantity" fill={BRAND} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5">
          <SectionHeader title="Категории по выручке" subtitle="Топ-8 категорий" />
          <div className="divide-y divide-slate-100">
            {data.topCategories.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Нет данных за период</div>
            ) : (
              data.topCategories.map((c, idx) => {
                const max = data.topCategories[0]?.revenue ?? 1;
                const share = (c.revenue / max) * 100;
                return (
                  <div key={c.id} className="py-3">
                    <div className="flex justify-between items-center text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 text-xs flex items-center justify-center font-semibold">
                          {idx + 1}
                        </span>
                        <span className="font-medium">{c.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatRub(c.revenue)}</div>
                        <div className="text-xs text-slate-400">{c.quantity} шт</div>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${share}%`,
                          background: PALETTE[idx % PALETTE.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Вспомогательные UI ──────────────────────────────────────

const METHOD_META: Record<
  string,
  { icon: React.ReactNode; bg: string; text: string; accent: string }
> = {
  CASH:    { icon: <Banknote size={16} />,   bg: 'bg-indigo-50',  text: 'text-indigo-700',  accent: 'bg-indigo-500' },
  CARD:    { icon: <CreditCard size={16} />, bg: 'bg-emerald-50', text: 'text-emerald-700', accent: 'bg-emerald-500' },
  QRCODE:  { icon: <QrCode size={16} />,     bg: 'bg-amber-50',   text: 'text-amber-700',   accent: 'bg-amber-500' },
  TRANSFER:{ icon: <Send size={16} />,       bg: 'bg-orange-50',  text: 'text-orange-700',  accent: 'bg-orange-500' },
  ONLINE:  { icon: <Smartphone size={16} />, bg: 'bg-rose-50',    text: 'text-rose-700',    accent: 'bg-rose-500' },
  MIXED:   { icon: <Layers size={16} />,     bg: 'bg-sky-50',     text: 'text-sky-700',     accent: 'bg-sky-500' },
};

function PaymentMethodsTiles({
  methods,
}: {
  methods: AnalyticsPayload['paymentMethods'];
}) {
  const active = methods.filter((m) => m.amount > 0 || m.count > 0);
  const total = active.reduce((s, m) => s + m.amount, 0);
  const totalCount = active.reduce((s, m) => s + m.count, 0);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold">Оплаты по способам</h3>
          <p className="text-xs text-slate-500">Только оплаченные</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Всего</div>
          <div className="font-semibold tabular-nums">
            {formatRub(total)}{' '}
            <span className="text-xs text-slate-400 font-normal">· {totalCount} оплат</span>
          </div>
        </div>
      </div>

      {active.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
          Оплат за период нет
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {active.map((m) => {
            const meta = METHOD_META[m.method] ?? {
              icon: <Banknote size={16} />,
              bg: 'bg-slate-50',
              text: 'text-slate-700',
              accent: 'bg-slate-400',
            };
            const share = total > 0 ? (m.amount / total) * 100 : 0;
            return (
              <div
                key={m.method}
                className="rounded-xl border border-slate-100 p-3 flex flex-col gap-2 hover:border-slate-200 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${meta.bg} ${meta.text}`}
                  >
                    {meta.icon}
                    {paymentMethodLabels[m.method] ?? m.method}
                  </span>
                  <span className="text-[11px] text-slate-400 tabular-nums">
                    {share.toFixed(0)}%
                  </span>
                </div>
                <div className="text-xl font-semibold tabular-nums leading-none">
                  {formatRub(m.amount)}
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {m.count} {declOrders(m.count)}
                </div>
                <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${meta.accent}`}
                    style={{ width: `${share}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function declOrders(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'оплата';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'оплаты';
  return 'оплат';
}

function KpiGrid({ data }: { data: AnalyticsPayload }) {
  const s = data.summary;
  const d = data.deltas;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Kpi
        title="Оборот"
        value={formatRub(s.revenue)}
        delta={d.revenue}
        icon={<Banknote size={20} />}
        tone="brand"
      />
      <Kpi
        title="Заказов"
        value={s.ordersCount.toString()}
        delta={d.ordersCount}
        icon={<ShoppingBasket size={20} />}
        tone="warning"
      />
      <Kpi
        title="Средний чек"
        value={formatRub(s.avgCheck)}
        delta={d.avgCheck}
        icon={<TrendingUp size={20} />}
        tone="success"
      />
      <Kpi
        title="Оплачено"
        value={s.paidOrders.toString()}
        icon={<Percent size={20} />}
        tone="slate"
        hint={`${s.ordersCount === 0 ? 0 : Math.round((s.paidOrders / s.ordersCount) * 100)}% оплачено`}
      />
      <Kpi
        title="Отменено"
        value={s.cancelledOrders.toString()}
        delta={d.cancelledOrders}
        invertDelta
        icon={<XCircle size={20} />}
        tone="danger"
      />
      <Kpi
        title="Клиенты"
        value={s.uniqueClients.toString()}
        icon={<UsersRound size={20} />}
        tone="slate"
        hint="Уникальных"
      />
      <Kpi
        title="Расходы"
        value={formatRub(s.expenses ?? 0)}
        delta={d.expenses}
        invertDelta
        icon={<Receipt size={20} />}
        tone="danger"
      />
      <Kpi
        title="Себестоимость"
        value={formatRub(s.cogs ?? 0)}
        icon={<PackageOpen size={20} />}
        tone="warning"
        hint={
          (s.cogs ?? 0) === 0
            ? 'Заполните закупочные цены блюд'
            : `Фудкост ${s.foodCostPercent ?? 0}%`
        }
      />
      <Kpi
        title="Валовая прибыль"
        value={formatRub(s.grossProfit ?? 0)}
        delta={d.grossProfit}
        icon={<TrendingUp size={20} />}
        tone={(s.grossProfit ?? 0) < 0 ? 'danger' : 'success'}
        hint={`Маржа ${s.marginPercent ?? 0}% · оборот минус закупка`}
      />
      <Kpi
        title="Чистая прибыль"
        value={formatRub(s.netProfit ?? s.net ?? s.revenue)}
        icon={<Wallet size={20} />}
        tone={(s.netProfit ?? s.net ?? s.revenue) < 0 ? 'danger' : 'success'}
        hint="Минус себестоимость и расходы"
      />
      <CostCoverageNote coverage={s.costCoverage} />
    </div>
  );
}

// Пока закупочные цены заполнены не у всех блюд, маржа завышена: у позиций без
// себестоимости COGS равен нулю. Честнее сказать об этом прямо, чем показывать
// красивую, но неверную цифру.
function CostCoverageNote({ coverage }: { coverage?: number }) {
  if (coverage == null || coverage >= 95) return null;
  return (
    <div className="col-span-2 md:col-span-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <Info size={16} className="mt-0.5 shrink-0" />
      <div>
        Себестоимость известна у <b>{coverage}%</b> продаж за период — маржа считается
        только по ним и выглядит выше реальной.{' '}
        <Link to="/menu" className="underline underline-offset-2">
          Заполните закупочные цены блюд
        </Link>{' '}
        — новые продажи начнут учитываться сразу.
      </div>
    </div>
  );
}

// Расходы за период: сумма, доля из кассы и разбивка по статьям.
function ExpensesBlock({ data }: { data: AnalyticsPayload }) {
  const e = data.expenses;
  if (!e) return null;
  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold">Расходы за период</h3>
          <p className="text-xs text-slate-500">
            {e.count} записей · из кассы {formatRub(e.fromKassa)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-2xl font-semibold text-rose-600 tabular-nums">{formatRub(e.total)}</div>
          <Link to="/expenses" className="btn-outline text-sm">Все расходы</Link>
        </div>
      </div>
      {e.byCategory.length === 0 ? (
        <div className="text-sm text-slate-400 py-4 text-center">
          За период расходов нет. Записывайте траты на странице «Расходы» — они попадут сюда и в кассовую смену.
        </div>
      ) : (
        <div className="grid gap-2">
          {e.byCategory.slice(0, 8).map((c) => {
            const share = e.total > 0 ? Math.round((c.total / e.total) * 100) : 0;
            return (
              <div key={c.id ?? 'none'} className="flex items-center gap-3">
                <div className="w-40 shrink-0 truncate text-sm text-slate-600">{c.name}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${share}%`, background: c.color ?? '#f43f5e' }}
                  />
                </div>
                <div className="w-28 text-right text-sm tabular-nums font-medium">{formatRub(c.total)}</div>
                <div className="w-10 text-right text-xs text-slate-400 tabular-nums">{share}%</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type Tone = 'brand' | 'warning' | 'success' | 'slate' | 'danger';
const toneClass: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-600',
  warning: 'bg-amber-50 text-amber-600',
  success: 'bg-emerald-50 text-emerald-600',
  slate: 'bg-slate-100 text-slate-600',
  danger: 'bg-rose-50 text-rose-600',
};

function Kpi({
  title,
  value,
  delta,
  icon,
  tone,
  hint,
  invertDelta,
}: {
  title: string;
  value: string;
  delta?: number;
  icon: React.ReactNode;
  tone: Tone;
  hint?: string;
  invertDelta?: boolean;
}) {
  const good = delta == null ? false : invertDelta ? delta < 0 : delta > 0;
  const bad = delta == null ? false : invertDelta ? delta > 0 : delta < 0;
  const color = good ? 'text-emerald-600' : bad ? 'text-rose-600' : 'text-slate-400';
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">{title}</div>
          <div className="text-2xl font-semibold mt-1">{value}</div>
        </div>
        <div className={`p-2 rounded-lg ${toneClass[tone]}`}>{icon}</div>
      </div>
      {delta != null ? (
        <div className={`mt-2 text-xs flex items-center gap-1 ${color}`}>
          {good && <ArrowUpRight size={12} />}
          {bad && <ArrowDownRight size={12} />}
          <span>{delta > 0 ? '+' : ''}{delta}% к пред. периоду</span>
        </div>
      ) : hint ? (
        <div className="mt-2 text-xs text-slate-400">{hint}</div>
      ) : null}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h3 className="font-semibold">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  );
}

function shortDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}
